/* eslint-disable max-lines */
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BoltIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CloudIcon,
  CurrencyEuroIcon,
  DevicePhoneMobileIcon,
  InformationCircleIcon
} from "@heroicons/react/24/outline";
import { FC } from "react";
import { Tooltip } from "react-tooltip";
import Box from "../ui/Box";
import SpinnerLoader from "../ui/SpinnerLoader";

interface IEsimSelectedSalesProps {
  currentValue?: string;
  ratePercentage?: string;
  loading?: boolean;
  selectPeriodCost?: number;
  selectedPeriodEsimCount?: number;
  selectedPeriodGbUsage?: number;
  selectedPeriodProfit?: number;
  selectedPeriodTopUpCount?: number;
  selectedPeriodTotalCount?: number;
  hasLocationZoneFilter?: boolean;
  hasSourceChannelFilter?: boolean;
}

const statCards = [
  {
    label: "Kosto",
    icon: CurrencyEuroIcon,
    valueKey: "selectPeriodCost",
    isCurrency: true,
    hideOnFilters: true
  },
  {
    label: "Fitimi",
    icon: ChartBarIcon,
    valueKey: "selectedPeriodProfit",
    isCurrency: true,
    hideOnFilters: true
  },
  {
    label: "Numri i eSIM-ve",
    icon: DevicePhoneMobileIcon,
    valueKey: "selectedPeriodEsimCount",
    isCurrency: false,
    hideOnFilters: false
  },
  {
    label: "Përdorimi i GB",
    icon: CloudIcon,
    valueKey: "selectedPeriodGbUsage",
    isCurrency: false,
    isGb: true,
    hideOnFilters: true
  },
  {
    label: "Numri i Top-up",
    icon: BoltIcon,
    valueKey: "selectedPeriodTopUpCount",
    isCurrency: false,
    hideOnFilters: false
  },
  {
    label: "Numri total i eSIM-ve",
    icon: CheckCircleIcon,
    valueKey: "selectedPeriodTotalCount",
    isCurrency: false,
    hideOnFilters: false
  }
];

const EsimSelectedSales: FC<IEsimSelectedSalesProps> = ({
  currentValue,
  ratePercentage,
  loading,
  selectPeriodCost,
  selectedPeriodEsimCount,
  selectedPeriodGbUsage,
  selectedPeriodProfit,
  selectedPeriodTopUpCount,
  selectedPeriodTotalCount,
  hasLocationZoneFilter = false,
  hasSourceChannelFilter = false
}) => {
  const statValues: Record<string, number | undefined> = {
    selectPeriodCost,
    selectedPeriodProfit,
    selectedPeriodEsimCount,
    selectedPeriodGbUsage,
    selectedPeriodTopUpCount,
    selectedPeriodTotalCount
  };

  const formatDataUsage = (valueInGb: number): string => {
    if (valueInGb >= 1024 * 1024) {
      const pbValue = valueInGb / (1024 * 1024);
      return `${pbValue.toLocaleString()} PB`;
    } else if (valueInGb >= 1024) {
      const tbValue = valueInGb / 1024;
      return `${tbValue.toLocaleString()} TB`;
    } else {
      return `${valueInGb.toLocaleString()} GB`;
    }
  };

  // Don't filter cards, show all but style differently when zone filter is active
  return (
    <Box
      shadow="shadow-xl"
      padding="px-4 sm:px-6 pt-6 pb-3.5"
      className="w-full h-full flex flex-col"
    >
      <div className="relative flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 tracking-tight">
            Shitjet e periudhës së zgjedhur
          </h3>
          {(hasLocationZoneFilter || hasSourceChannelFilter) && (
            <InformationCircleIcon
              className="w-5 h-5 text-blue-500 cursor-help"
              data-tooltip-id="filter-tooltip"
              data-tooltip-content={
                hasLocationZoneFilter && hasSourceChannelFilter
                  ? "Filtrimi sipas zonës së vendndodhjes dhe burimit çaktivizon " +
                    "llogaritjen e kostove dhe fitimit për të shmangur gabime në llogaritje."
                  : hasLocationZoneFilter
                  ? "Filtrimi sipas zonës së vendndodhjes çaktivizon llogaritjen e kostove " +
                    "dhe fitimit për të shmangur gabime në llogaritje."
                  : "Filtrimi sipas burimit çaktivizon llogaritjen e kostove dhe fitimit " +
                    "për të shmangur gabime në llogaritje."
              }
              data-tooltip-variant="dark"
            />
          )}
        </div>
        <Tooltip id="filter-tooltip" />

        {loading ? (
          <SpinnerLoader className="absolute top-0 w-full h-full flex justify-center items-center" />
        ) : (
          <div className="flex-1 flex flex-col ">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                <span className="text-base text-gray-600 font-medium mb-2 sm:mb-0">
                  Vlera totale
                </span>
                <div className="flex items-center">
                  <span className="text-2xl sm:text-3xl font-extrabold text-blue-700">
                    {typeof currentValue === "number"
                      ? (currentValue as number).toLocaleString("en-US", {
                          style: "currency",
                          currency: "EUR"
                        })
                      : currentValue}
                  </span>
                  {ratePercentage && (
                    <div
                      className={`ml-3 flex items-center text-sm sm:text-base font-semibold ${
                        ratePercentage.startsWith("-")
                          ? "text-red-500"
                          : "text-green-500"
                      }`}
                    >
                      {ratePercentage.startsWith("-") ? (
                        <ArrowDownIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                      ) : (
                        <ArrowUpIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                      )}
                      {ratePercentage}%
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full border-t border-gray-200 mb-2" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {statCards.map((card) => {
                const Icon = card.icon;
                const value = statValues[card.valueKey];
                const isDisabled =
                  (hasLocationZoneFilter || hasSourceChannelFilter) && card.hideOnFilters;

                return (
                  <div
                    key={card.label}
                    className={`shadow-sm p-3 sm:p-4 rounded-xl flex items-center gap-3 min-h-[70px] ${
                      isDisabled ? "bg-gray-50 opacity-60" : "bg-white/80"
                    }`}
                  >
                    <div
                      className={`rounded-full p-2 ${
                        isDisabled ? "bg-gray-200" : "bg-blue-100"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 sm:w-6 sm:h-6 ${
                          isDisabled ? "text-gray-400" : "text-blue-600"
                        }`}
                      />
                    </div>
                    <div className="flex flex-col">
                      <span
                        className={`text-xs mb-1 font-medium ${
                          isDisabled ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {card.label}
                      </span>
                      <span
                        className={`text-base sm:text-lg font-bold ${
                          isDisabled ? "text-gray-400" : "text-gray-900"
                        }`}
                      >
                        {isDisabled ? (
                          <span>-</span>
                        ) : value !== undefined && value !== null ? (
                          card.isCurrency ? (
                            value.toLocaleString("en-US", {
                              style: "currency",
                              currency: "EUR"
                            })
                          ) : card.isGb ? (
                            formatDataUsage(value)
                          ) : (
                            value.toLocaleString()
                          )
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

export default EsimSelectedSales;
